// src/pages/pengamatan/buah-alami/detail-tabs/ProsentaseTab.jsx
import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper
} from '@mui/material';
import ProsentaseTableHeader from './ProsentaseTableHeader';

const ProsentaseTab = ({ pengamatanData, filteredPlots }) => {
    // Fungsi untuk menghilangkan angka di belakang titik
    const removeDecimal = (value) => {
        if (value === '-' || value === '' || value === null || value === undefined) return '-';
        
        const stringValue = value.toString();
        if (stringValue.includes('.')) {
            return stringValue.split('.')[0];
        }
        return stringValue;
    };

    // Fungsi untuk menghitung prosentase fase bunga
    const calculateFaseBungaPercentage = (plotData, fieldName) => {
        const faseBunga = plotData.data_entry?.fase_bunga || {};
        const total = parseFloat(plotData.data_entry?.total_ba) || 0;
        const fieldValue = parseFloat(faseBunga[fieldName]) || 0;
        
        if (total > 0) {
            return ((fieldValue / total) * 100).toFixed(2) + '%';
        }
        return '0.00%';
    };

    // Fungsi untuk menghitung prosentase tanaman
    const calculateTanamanPercentage = (plotData, fieldName) => {
        const tanaman = plotData.data_entry?.tanaman || {};
        const penyakit = plotData.data_entry?.tanaman?.penyakit_tanaman || {};
        const total = parseFloat(plotData.data_entry?.total_ba) || 0;
        
        let fieldValue = 0;
        
        // Cek apakah field ada di tanaman utama atau di penyakit
        if (fieldName.includes('penyakit_')) {
            const penyakitField = fieldName.replace('penyakit_', '');
            fieldValue = parseFloat(penyakit[`${penyakitField}_pt_ba`]) || 0;
        } else {
            fieldValue = parseFloat(tanaman[`${fieldName}_t_ba`]) || 0;
        }
        
        if (total > 0) {
            return ((fieldValue / total) * 100).toFixed(2) + '%';
        }
        return '0.00%';
    };

    // FUNGSI BARU: Menghitung prosentase buah alami dan mati
    const calculateBuahAlamiMatiPercentage = (plotData, fieldName) => {
        const dataEntry = plotData.data_entry || {};
        const total = parseFloat(dataEntry.total_ba) || 0;
        
        let fieldValue = 0;
        
        if (fieldName === 'buah_alami_ba') {
            fieldValue = parseFloat(dataEntry.buah_alami_ba) || 0;
        } else if (fieldName === 'mati_ba') {
            fieldValue = parseFloat(dataEntry.mati_ba) || 0;
        } else if (fieldName === 'spot_mati') {
            fieldValue = parseFloat(dataEntry.spot_mati_ba) || 0;
        }
        
        if (total > 0) {
            return ((fieldValue / total) * 100).toFixed(2) + '%';
        }
        return '0.00%';
    };

    // FUNGSI BARU: Menghitung prosentase bunga pinggiran
    const calculateBungaPinggiranPercentage = (plotData, fieldName) => {
        const tanamanPinggiran = plotData.data_entry?.tanaman_pinggiran || {};
        const total_tp_ba = parseFloat(tanamanPinggiran.total_tp_ba) || 0;
        
        let fieldValue = 0;
        
        if (fieldName === 'normal') {
            fieldValue = parseFloat(tanamanPinggiran.normal_tp_ba) || 0;
        } else if (fieldName === 'buah_alami') {
            fieldValue = parseFloat(tanamanPinggiran.buah_alami_tp_ba) || 0;
        }
        
        if (total_tp_ba > 0) {
            return ((fieldValue / total_tp_ba) * 100).toFixed(2) + '%';
        }
        return '0.00%';
    };

    // Fungsi untuk mengambil data buah alami dari struktur Firebase - DIPERBARUI
    const getBuahAlamiData = (plotData) => {
        return {
            // SEKARANG MENGGUNAKAN PERSENTASE, BUKAN NILAI ABSOLUT
            buah_alami: calculateBuahAlamiMatiPercentage(plotData, 'buah_alami_ba'),
            mati: calculateBuahAlamiMatiPercentage(plotData, 'mati_ba'),
            total: removeDecimal(plotData.data_entry?.total_ba) || '-',
            spot_mati: calculateBuahAlamiMatiPercentage(plotData, 'spot_mati')
        };
    };

    // Fungsi untuk mengambil data fase bunga dari struktur Firebase
    const getFaseBungaData = (plotData) => {
        const faseBunga = plotData.data_entry?.fase_bunga || {};
        return {
            open_heart: calculateFaseBungaPercentage(plotData, 'open_heart_fb_ba'),
            red_bud_125: calculateFaseBungaPercentage(plotData, 'red_bud_125_fb_ba'),
            red_bud_254: calculateFaseBungaPercentage(plotData, 'red_bud_254_fb_ba'),
            early_cones: calculateFaseBungaPercentage(plotData, 'early_cones_fb_ba'),
            mid_cones: calculateFaseBungaPercentage(plotData, 'mid_cones_fb_ba'),
            late_cones: calculateFaseBungaPercentage(plotData, 'late_cones_fb_ba'),
            early_petals: calculateFaseBungaPercentage(plotData, 'early_petals_fb_ba'),
            mid_petals: calculateFaseBungaPercentage(plotData, 'mid_petals_fb_ba'),
            late_petals: calculateFaseBungaPercentage(plotData, 'late_petals_fb_ba'),
            dry_petals: calculateFaseBungaPercentage(plotData, 'dry_petals_fb_ba'),
            filling_stage_1: calculateFaseBungaPercentage(plotData, 'filling_stage_1_fb_ba'),
            filling_stage_2: calculateFaseBungaPercentage(plotData, 'filling_stage_2_fb_ba'),
            sc0: calculateFaseBungaPercentage(plotData, 'sc0_fb_ba'),
            sc0_lebih: calculateFaseBungaPercentage(plotData, 'sc0_ld_fb_ba'),
            terpetik: calculateFaseBungaPercentage(plotData, 'terpetik_fb_ba'),
            dimakan_tikus: calculateFaseBungaPercentage(plotData, 'dimakan_tikus_fb_ba')
        };
    };

    // Fungsi untuk mengambil data tanaman dari struktur Firebase
    const getTanamanData = (plotData) => {
        return {
            normal: calculateTanamanPercentage(plotData, 'normal'),
            penyakit_phy: calculateTanamanPercentage(plotData, 'penyakit_phy'),
            penyakit_erw: calculateTanamanPercentage(plotData, 'penyakit_erw'),
            kerdil: calculateTanamanPercentage(plotData, 'kerdil')
        };
    };

    // FUNGSI BARU: Mengambil data bunga pinggiran
    const getBungaPinggiranData = (plotData) => {
        return {
            normal_pinggiran: calculateBungaPinggiranPercentage(plotData, 'normal'),
            alami_pinggiran: calculateBungaPinggiranPercentage(plotData, 'buah_alami')
        };
    };

    // FUNGSI BARU: Menghitung total data untuk perhitungan prosentase total
const calculateTotals = () => {
    const plotsToUse = filteredPlots && filteredPlots.length > 0 
        ? filteredPlots 
        : (pengamatanData.plot ? Object.entries(pengamatanData.plot).map(([plotKey, plotData]) => ({
            plotKey,
            ...plotData
        })) : []);

    const totals = {
        // TAMBAHKAN TOTAL LUAS DAN PJP
        luas_plot: 0,
        pjp: 0,
        
        faseBunga: {
            open_heart: 0,
            red_bud_125: 0,
            red_bud_254: 0,
            early_cones: 0,
            mid_cones: 0,
            late_cones: 0,
            early_petals: 0,
            mid_petals: 0,
            late_petals: 0,
            dry_petals: 0,
            filling_stage_1: 0,
            filling_stage_2: 0,
            sc0: 0,
            sc0_lebih: 0,
            terpetik: 0,
            dimakan_tikus: 0
        },
        tanaman: {
            normal: 0,
            penyakit_phy: 0,
            penyakit_erw: 0,
            kerdil: 0
        },
        buahAlami: {
            buah_alami: 0,
            mati: 0,
            total: 0,
            spot_mati: 0
        },
        tanamanPinggiran: {
            normal: 0,
            buah_alami: 0,
            total: 0
        }
    };

    plotsToUse.forEach(plot => {
        const dataEntry = plot.data_entry || {};
        const faseBunga = dataEntry.fase_bunga || {};
        const tanaman = dataEntry.tanaman || {};
        const penyakit = tanaman.penyakit_tanaman || {};
        const tanamanPinggiran = dataEntry.tanaman_pinggiran || {};

        // TAMBAHKAN AKUMULASI LUAS DAN PJP
        totals.luas_plot += parseFloat(plot.luas_plot) || 0;
        totals.pjp += parseFloat(plot.pjp) || 0;

        // Akumulasi data fase bunga
        Object.keys(totals.faseBunga).forEach(key => {
            const fieldName = `${key}_fb_ba`;
            totals.faseBunga[key] += parseFloat(faseBunga[fieldName]) || 0;
        });

        // Akumulasi data tanaman
        totals.tanaman.normal += parseFloat(tanaman.normal_t_ba) || 0;
        totals.tanaman.penyakit_phy += parseFloat(penyakit.phy_pt_ba) || 0;
        totals.tanaman.penyakit_erw += parseFloat(penyakit.erw_pt_ba) || 0;
        totals.tanaman.kerdil += parseFloat(tanaman.kerdil_t_ba) || 0;

        // Akumulasi data buah alami
        totals.buahAlami.buah_alami += parseFloat(dataEntry.buah_alami_ba) || 0;
        totals.buahAlami.mati += parseFloat(dataEntry.mati_ba) || 0;
        totals.buahAlami.total += parseFloat(dataEntry.total_ba) || 0;
        totals.buahAlami.spot_mati += parseFloat(dataEntry.spot_mati_ba) || 0;

        // Akumulasi data tanaman pinggiran
        totals.tanamanPinggiran.normal += parseFloat(tanamanPinggiran.normal_tp_ba) || 0;
        totals.tanamanPinggiran.buah_alami += parseFloat(tanamanPinggiran.buah_alami_tp_ba) || 0;
        totals.tanamanPinggiran.total += parseFloat(tanamanPinggiran.total_tp_ba) || 0;
    });

    return totals;
};

    // FUNGSI BARU: Menghitung prosentase untuk data total
const calculateTotalPercentage = (totals) => {
    const totalBuahAlami = totals.buahAlami.total;  // $AN33 = 1301
    
    return {
        // Fase Bunga: Total_FaseBunga / Total_BuahAlami × 100
        faseBunga: {
            open_heart: totalBuahAlami > 0 ? ((totals.faseBunga.open_heart / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            red_bud_125: totalBuahAlami > 0 ? ((totals.faseBunga.red_bud_125 / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            red_bud_254: totalBuahAlami > 0 ? ((totals.faseBunga.red_bud_254 / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',  // 5/1301×100=0.38%
            early_cones: totalBuahAlami > 0 ? ((totals.faseBunga.early_cones / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            mid_cones: totalBuahAlami > 0 ? ((totals.faseBunga.mid_cones / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            late_cones: totalBuahAlami > 0 ? ((totals.faseBunga.late_cones / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            early_petals: totalBuahAlami > 0 ? ((totals.faseBunga.early_petals / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            mid_petals: totalBuahAlami > 0 ? ((totals.faseBunga.mid_petals / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            late_petals: totalBuahAlami > 0 ? ((totals.faseBunga.late_petals / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            dry_petals: totalBuahAlami > 0 ? ((totals.faseBunga.dry_petals / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            filling_stage_1: totalBuahAlami > 0 ? ((totals.faseBunga.filling_stage_1 / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            filling_stage_2: totalBuahAlami > 0 ? ((totals.faseBunga.filling_stage_2 / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            sc0: totalBuahAlami > 0 ? ((totals.faseBunga.sc0 / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            sc0_lebih: totalBuahAlami > 0 ? ((totals.faseBunga.sc0_lebih / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            terpetik: totalBuahAlami > 0 ? ((totals.faseBunga.terpetik / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            dimakan_tikus: totalBuahAlami > 0 ? ((totals.faseBunga.dimakan_tikus / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%'
        },

        // Tanaman: Total_Tanaman / Total_BuahAlami × 100
        tanaman: {
            normal: totalBuahAlami > 0 ? ((totals.tanaman.normal / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            penyakit_phy: totalBuahAlami > 0 ? ((totals.tanaman.penyakit_phy / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            penyakit_erw: totalBuahAlami > 0 ? ((totals.tanaman.penyakit_erw / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            kerdil: totalBuahAlami > 0 ? ((totals.tanaman.kerdil / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%'
        },

        // Buah Alami: Total_BuahAlami / Total_BuahAlami × 100 (akan selalu 100% untuk buah_alami)
        buahAlami: {
            buah_alami: totalBuahAlami > 0 ? ((totals.buahAlami.buah_alami / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            mati: totalBuahAlami > 0 ? ((totals.buahAlami.mati / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%',
            spot_mati: totalBuahAlami > 0 ? ((totals.buahAlami.spot_mati / totalBuahAlami) * 100).toFixed(2) + '%' : '0.00%'
        },

        // Bunga Pinggiran: Total_BungaPinggiran / Total_BungaPinggiran × 100
        bungaPinggiran: {
            normal_pinggiran: totals.tanamanPinggiran.total > 0 ? ((totals.tanamanPinggiran.normal / totals.tanamanPinggiran.total) * 100).toFixed(2) + '%' : '0.00%',
            alami_pinggiran: totals.tanamanPinggiran.total > 0 ? ((totals.tanamanPinggiran.buah_alami / totals.tanamanPinggiran.total) * 100).toFixed(2) + '%' : '0.00%'
        }
    };
};

    const calculatePercentages = () => {
        // GUNAKAN filteredPlots JIKA ADA, JIKA TIDAK GUNAKAN SEMUA PLOT
        const plotsToUse = filteredPlots && filteredPlots.length > 0 
            ? filteredPlots 
            : (pengamatanData.plot ? Object.entries(pengamatanData.plot).map(([plotKey, plotData]) => ({
                plotKey,
                ...plotData
            })) : []);
        
        return plotsToUse
            .map((plot) => {
                const dataEntry = plot.data_entry || {};
                
                // Ambil data untuk header yang sama dengan Data Observasi
                const buahAlamiData = getBuahAlamiData(plot);
                const faseBungaData = getFaseBungaData(plot);
                const tanamanData = getTanamanData(plot);
                const bungaPinggiranData = getBungaPinggiranData(plot);
                
                return {
                    plot: plot.plot || plot.plotKey,
                    luas_plot: plot.luas_plot || '-',
                    pjp: plot.pjp || '-',
                    // Fase Bunga
                    ...faseBungaData,
                    // Tanaman
                    ...tanamanData,
                    // Buah Alami - SEKARANG SUDAH DALAM BENTUK PERSENTASE
                    ...buahAlamiData,
                    // Bunga Pinggiran - PERSENTASE
                    ...bungaPinggiranData,
                    // Kolom prosentase tambahan (jika masih diperlukan)
                    percentageBuahAlami: buahAlamiData.buah_alami, // sama dengan buah_alami
                    percentageMati: buahAlamiData.mati // sama dengan mati
                };
            })
            .sort((a, b) => {
                if (!isNaN(a.plot) && !isNaN(b.plot)) {
                    return parseInt(a.plot) - parseInt(b.plot);
                }
                return a.plot.localeCompare(b.plot);
            });
    };

    const percentages = calculatePercentages();
    const totals = calculateTotals();
    const totalPercentage = calculateTotalPercentage(totals);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Data Prosentase
            </Typography>
            
            <Card>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 1800 }}>
                            <ProsentaseTableHeader />
                            <TableBody>
                                {percentages.map((item, index) => (
                                    <TableRow key={index}>
                                        {/* Plot */}
                                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                            {item.plot}
                                        </TableCell>
                                        
                                        {/* Luas */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.luas_plot}
                                        </TableCell>
                                        
                                        {/* PJP */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.pjp}
                                        </TableCell>
                                        
                                        {/* Fase Bunga - Data */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.open_heart}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.red_bud_125}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.red_bud_254}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.early_cones}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.mid_cones}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.late_cones}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.early_petals}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.mid_petals}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.late_petals}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.dry_petals}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.filling_stage_1}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.filling_stage_2}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.sc0}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.sc0_lebih}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.terpetik}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.dimakan_tikus}
                                        </TableCell>
                                        
                                        {/* Tanaman - Data */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.normal}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.penyakit_phy}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.penyakit_erw}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.kerdil}
                                        </TableCell>

                                        {/* Buah Alami - Data (SEKARANG DALAM PERSENTASE) */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.buah_alami}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.mati}
                                        </TableCell>

                                        {/* Bunga Pinggiran - Data (PERSENTASE) */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {item.normal_pinggiran}
                                        </TableCell>
                                        <TableCell>
                                            {item.alami_pinggiran}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* BARIS TOTAL BARU */}
                                {percentages.length > 0 && (
                                    <TableRow sx={{ backgroundColor: '#f5f5f5', '& td': { fontWeight: 'bold', borderTop: '2px solid #e0e0e0' } }}>
                                        {/* Plot - Total */}
                                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                            Total
                                        </TableCell>
                                        
                                        {/* Luas - Total */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totals.luas_plot > 0 ? totals.luas_plot.toFixed(2) : '-'}
                                        </TableCell>
                                        
                                        {/* PJP - Total */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totals.pjp > 0 ? totals.pjp.toFixed(0) : '-'}
                                        </TableCell>
                                        
                                        {/* Fase Bunga - Total Prosentase */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.open_heart || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.red_bud_125 || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.red_bud_254 || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.early_cones || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.mid_cones || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.late_cones || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.early_petals || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.mid_petals || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.late_petals || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.dry_petals || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.filling_stage_1 || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.filling_stage_2 || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.sc0 || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.sc0_lebih || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.terpetik || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.faseBunga?.dimakan_tikus || '0.00%'}
                                        </TableCell>
                                        
                                        {/* Tanaman - Total Prosentase */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.tanaman?.normal || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.tanaman?.penyakit_phy || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.tanaman?.penyakit_erw || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.tanaman?.kerdil || '0.00%'}
                                        </TableCell>

                                        {/* Buah Alami - Total Prosentase */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.buahAlami?.buah_alami || '0.00%'}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.buahAlami?.mati || '0.00%'}
                                        </TableCell>

                                        {/* Bunga Pinggiran - Total Prosentase */}
                                        <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                            {totalPercentage.bungaPinggiran?.normal_pinggiran || '0.00%'}
                                        </TableCell>
                                        <TableCell>
                                            {totalPercentage.bungaPinggiran?.alami_pinggiran || '0.00%'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {percentages.length === 0 && (
                        <Typography color="text.secondary" fontStyle="italic" sx={{ mt: 2 }}>
                            Tidak ada data untuk menghitung prosentase
                        </Typography>
                    )}
            </Card>
        </Box>
    );
};

export default ProsentaseTab;